import type { JSX, ReactNode } from 'react';
import RefreshIcon from '@assets/icons/learning-overview/quiz/refresh-cw.svg';
import BellIcon from '@assets/icons/notifications/bell.svg';
import CheckIcon from '@assets/icons/notifications/check.svg';
import ExclamationIcon from '@assets/icons/notifications/exclamation.svg';
import MinusIcon from '@assets/icons/notifications/minus.svg';
import WrongIcon from '@assets/icons/notifications/no-entry.svg';
import PlusIcon from '@assets/icons/notifications/plus.svg';
import InfoIcon from '@assets/icons/notifications/small-info.svg';
import UnlinkIcon from '@assets/icons/notifications/unlink.svg';
import { useLingui } from '@lingui/react/macro';
import { PATHWAY_TYPES } from '@routes/sidebar/admin-center/creator/___pages___/pathways/ui/form/constants';
import { Pill, PillIcon, PillText } from '@routes/sidebar/notifications/ui/tag-pill/styled.tsx';

const typeIconMap: Record<string, JSX.Element> = {
    Assigned: <PlusIcon />,
    Passed: <CheckIcon />,
    Completed: <CheckIcon />,
    Reminder: <BellIcon />,
    FinalReminder: <BellIcon />,
    Failed: <WrongIcon />,
    Aborted: <WrongIcon />,
    Overdue: <ExclamationIcon />,
    TaskRedo: <WrongIcon />,
    TaskAssessorChanged: <WrongIcon />,
    TaskExempted: <RefreshIcon />,
    TaskRecalledCrewSignOff: <ExclamationIcon />,
    TaskRecalledAssessorSignOff: <ExclamationIcon />,
    TaskRecalledElementUpdated: <ExclamationIcon />,
    TaskRecalledElementDeleted: <ExclamationIcon />,
    TaskRecalledAssessorUnavailable: <ExclamationIcon />,
    TaskReceivedForEvaluation: <CheckIcon />,
    TaskReceivedForExemption: <CheckIcon />,
    TaskRecalledByAssessee: <RefreshIcon />,
    TaskClosed: <CheckIcon />,
    LearningReassignedCategoryChanged: <RefreshIcon />,
    PathwayUnsubscribed: <UnlinkIcon />,
    PathwayUnasigned: <MinusIcon />,
};

const getPillIcon = (type: string): JSX.Element => {
    return typeIconMap[type] || <InfoIcon />;
};

export const PillTag = ({ type, source }: { type: string; source: string }) => {
    const { t } = useLingui();

    const SOURCE_TO_TEXT: Record<string, string> = {
        [PATHWAY_TYPES.LEARNING]: t`Learning`,
        [PATHWAY_TYPES.ASSESSMENT]: t`Assessment`,
        [PATHWAY_TYPES.SURVEY]: t`Survey`,
        TaskEvaluation: t`Task-Evaluation`,
        New: t`New`,
        System: t`System`,
    };
    return (
        <Pill $hasIcon={false} $source={source} $type={type}>
            <PillText $source={source} $type={type}>
                {SOURCE_TO_TEXT[source] ?? source}
            </PillText>
        </Pill>
    );
};

export const PillTagWithIcon = ({ type, source }: { type: string; source: string }) => {
    const { t } = useLingui();

    const TYPE_TO_TEXT: Record<string, ReactNode> = {
        Assigned: t`Pathway Assigned`,
        Passed: t`Passed`,
        Completed: t`Completed`,
        Reminder: t`Reminder`,
        FinalReminder: t`Final Reminder`,
        Failed: t`Failed`,
        Aborted: t`Aborted`,
        Overdue: t`Overdue`,
        Info: t`Info`,
        AdminCompletedSubmittedRequest: t`Info`,
        Security: t`Security`,
        PasswordChanged: t`Security`,
        Maintenance: t`Maintenance`,
        TaskRedo: t`Re-do`,
        TaskAssessorChanged: t`Assessor Changed`,
        TaskRecalledCrewSignOff: t`Auto-recalled - Crew sign off`,
        TaskExempted: t`Exempted`,
        TaskRecalledAssessorSignOff: t`Auto-recalled - Assessor sign off`,
        TaskRecalledElementUpdated: t`Auto-recalled - Task element updated`,
        TaskRecalledElementDeleted: t`Auto-recalled - Task element deleted`,
        TaskRecalledAssessorUnavailable: t`Auto-recalled - Shore assessor unavailable`,
        TaskExemptionRejected: t`Exemption Rejected`,
        TaskReceivedForEvaluation: t`Received for evaluation`,
        TaskReceivedForExemption: t`Received for exemption`,
        TaskRecalledByAssessee: t`Recalled by Assessee`,
        TaskClosed: t`Closed`,
        LearningReassignedCategoryChanged: (
            <>
                <strong>{t`Reassigned`}</strong>: {t`Learning Category changed`}
            </>
        ),
        PathwayUnsubscribed: <> {t`Pathway Unsubscribed`}</>,
        PathwayUnasigned: <> {t`Pathway Unassigned`}</>,
    };
    return (
        <Pill $hasIcon={true} $source={source} $type={type}>
            <PillIcon $source={source} $type={type}>
                {getPillIcon(type)}
            </PillIcon>
            <PillText $source={source} $type={type}>
                {TYPE_TO_TEXT[type]}
            </PillText>
        </Pill>
    );
};
